/**
 * Resolve the rendered state of a maker on/off switch (Poly / Kalshi) on the
 * opener row.
 *
 * A maker toggle fires up to three sequential PATCHes behind the scenes
 * (auto-enable the pair → create the rule → set the flag). Binding the switch
 * straight to the rule's flag made it look frozen until that whole chain
 * settled ("no cambia hasta después"). Instead the click records an optimistic
 * `pending` value synchronously; this helper makes the switch reflect it
 * immediately and lock against double-clicks until the mutation settles, at
 * which point `pending` is cleared and the switch hands off to server truth.
 *
 * - `pending === undefined` → no in-flight toggle; show the rule's flag.
 * - `pending` set → show the clicked value and disable until it clears.
 * - `activationBlocked` (ungrouped pair, arming) always disables.
 * - `ruleUnknown` → the rules query has no answer for this row YET; the switch
 *   renders as UNKNOWN, never as OFF (see below).
 *
 * `ruleUnknown` closes the silent-OFF hole. `makerOn` is derived as
 * `rule?.[flag] === true`, so an ABSENT rule and a rule with the flag off
 * collapse to the same `false` — and the rules query is absent for real
 * windows: a cold load, and every group switch (`keepPreviousData` serves the
 * PREVIOUS group's rules, which miss on every row of the new one). Measured
 * 2026-08-24 that window was 8-30 s, and the operator read a fully armed slate
 * as OFF. It also mattered beyond display: clicking a switch with no bound
 * rule sends `doSetMakerFlag` down the CREATE branch for a pair that already
 * has a rule. Rendering unknown is what stops both.
 */
export function resolveMakerSwitchState(opts: {
  pending: boolean | undefined;
  makerOn: boolean;
  activationBlocked: boolean;
  ruleUnknown?: boolean;
}): { checked: boolean; disabled: boolean; unknown: boolean } {
  const { pending, makerOn, activationBlocked, ruleUnknown } = opts;
  // A pending click is the operator's own intent and outranks the missing
  // rule — it is the one thing we do know about this switch.
  const unknown = ruleUnknown === true && pending === undefined;
  return {
    checked: pending ?? makerOn,
    disabled: unknown || activationBlocked || pending !== undefined,
    unknown,
  };
}
